import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, LayoutGrid } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NavItem } from "./useNavLinks";

// Um tópico do menu (coluna da esquerda) e os links dele (colunas da direita).
export type MenuGroup = {
  key: string;
  title: string;
  icon: LucideIcon;
  to: string;
  items: { key: string; label: string; to: string }[];
};

type HeaderMenuProps = {
  item: NavItem;
  groups: MenuGroup[];
  /** Primeiro tópico, em rosa: leva para a página inteira ("Todas as soluções", "Todos os cases"). */
  all: { label: string; to: string };
};

// Tempo para o mouse atravessar o vão entre o link e o painel sem fechar o menu.
const CLOSE_DELAY = 150;

// Menu do header (desktop) para "Soluções" e "Cases": abre ao passar o mouse ou focar pelo teclado.
// Tópicos à esquerda (passar o mouse destaca o grupo) e os itens de cada tópico à direita.
export default function HeaderMenu({ item, groups, all }: HeaderMenuProps) {
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const panelId = `cz-${item.menu}-panel`;

  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const show = () => {
    cancelClose();
    setOpen(true);
  };
  const close = () => {
    cancelClose();
    setOpen(false);
    setActiveKey(null);
  };
  const closeLater = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(close, CLOSE_DELAY);
  };

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div
      className="cz-solutions"
      onMouseEnter={show}
      onMouseLeave={closeLater}
      onFocus={show}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      <NavLink
        to={item.to ?? all.to}
        className={`cz-header__link cz-solutions__trigger${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={close}
      >
        {item.label}
        <ChevronDown size={16} className="cz-solutions__chevron" aria-hidden="true" />
      </NavLink>

      <div id={panelId} className="cz-solutions__panel" hidden={!open}>
        <div className="cz-solutions__inner">
          <ul className="cz-solutions__topics" onMouseLeave={() => setActiveKey(null)}>
            <li>
              <Link to={all.to} className="cz-solutions__topic cz-solutions__topic--all" onClick={close}>
                <LayoutGrid size={20} aria-hidden="true" />
                <span>{all.label}</span>
                <ChevronRight size={16} className="cz-solutions__topic-arrow" aria-hidden="true" />
              </Link>
            </li>
            {groups.map(({ key, title, icon: Icon, to }) => (
              <li key={key}>
                <Link
                  to={to}
                  className={`cz-solutions__topic${activeKey === key ? " is-active" : ""}`}
                  onMouseEnter={() => setActiveKey(key)}
                  onFocus={() => setActiveKey(key)}
                  onClick={close}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span>{title}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="cz-solutions__groups">
            {groups.map(({ key, title, to, items }) => (
              <div key={key} className={`cz-solutions__group${activeKey === key ? " is-active" : ""}`}>
                <Link to={to} className="cz-solutions__group-title" onClick={close}>
                  {title}
                </Link>
                <ul>
                  {items.map((it) => (
                    <li key={it.key}>
                      <Link to={it.to} className="cz-solutions__link" onClick={close}>
                        {it.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
